/**
 * 組織 OrganisationsTab (S10.1) — 母/子公司 (parent/subsidiary) + 直營/加盟
 * (company-owned/franchise), CRUD-lite. Deactivate is reason-gated
 * (ReasonRequiredModal → audit row).
 *
 * S2.1 (rbacV2) — behind `governance.rbacV2` the flat table becomes the
 * `OrgTreeEditor` hierarchy tree (tree-first, the category-pattern spine for
 * S2.4 node-scoped assignment): inline add-child pre-fills the create modal's
 * parent, drag re-parent is reason-gated (the handler recomputes `path` +
 * descendant depth), and every capability of the flat view (create,
 * activate/deactivate) stays available — the tree only ADDS affordances.
 * Flag OFF renders the flat table byte-identical to today (zero visible change).
 */

import { useMemo, useState } from 'react';
import {
  App,
  Button,
  Dropdown,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { clearCache } from 'ahooks';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import StatusTag from '@/components/common/StatusTag';
import { useFeature } from '@/features/flags';
import apiClient, { getUserProfile } from '@/services/api/axios.config';
import type { User } from '@/models/user';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useOrganisations, useSystemMutations } from './useSystemGovernance';
import OrgTreeEditor from './OrgTreeEditor';
import type { OrgMoveIntent } from './OrgTreeEditor';
import type { OrganisationUnit, OrgKind, OrgModel } from './model';

const { Text } = Typography;

const KINDS: readonly OrgKind[] = ['headquarters', 'subsidiary'];
const MODELS: readonly OrgModel[] = ['direct', 'franchise'];

/**
 * Slice-local re-parent mutation (S2.1). `systemApi` / `useSystemGovernance`
 * are owned by the concurrent S2.3 / S2.2 slices, so the PATCH + cache
 * invalidation live here for now — same keys, same store-first discipline.
 * Follow-up: fold into `systemApi.reparentOrganisation` once those files free.
 */
const ORG_CACHE_KEY = '/api/governance/organisations';
const AUDIT_CACHE_KEY = '/api/governance/audit-logs';

async function reparentOrganisation(
  id: string,
  parentId: string | null,
  reason: string,
  actorName?: string
): Promise<OrganisationUnit> {
  const { data } = await apiClient.patch<OrganisationUnit>(`/governance/organisations/${id}`, {
    parentId,
    reason,
    actorName,
  });
  clearCache(ORG_CACHE_KEY);
  clearCache(AUDIT_CACHE_KEY);
  return data;
}

interface OrgFormData {
  code: string;
  name: string;
  kind: OrgKind;
  model: OrgModel;
  parentId?: string;
}

const OrganisationsTab = () => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { message } = App.useApp();
  const rbacV2 = useFeature('governance.rbacV2');
  const { organisations, loading, refresh } = useOrganisations();
  const { createOrganisation, setOrganisationStatus } = useSystemMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<OrganisationUnit | null>(null);
  const [moveIntent, setMoveIntent] = useState<OrgMoveIntent | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().trim().min(1, t('validation.codeRequired')),
        name: z.string().trim().min(1, t('validation.nameRequired')),
        kind: z.enum(['headquarters', 'subsidiary']),
        model: z.enum(['direct', 'franchise']),
        parentId: z.string().optional(),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    trigger,
  } = useForm<OrgFormData>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', name: '', kind: 'subsidiary', model: 'direct', parentId: undefined },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  /** Tree add-child pre-fills the parent (CreateNodeModal pattern); header CTA opens blank. */
  const openCreate = (parentId?: string) => {
    reset({ code: '', name: '', kind: 'subsidiary', model: 'direct', parentId });
    setCreateOpen(true);
  };

  const onCreate = handleSubmit(async (data) => {
    await createOrganisation(data);
    message.success(t('messages.orgCreated'));
    setCreateOpen(false);
    refresh();
  });

  const submitStatus = async (reason: string) => {
    if (!statusTarget) return;
    const next = statusTarget.status === 'active' ? 'inactive' : 'active';
    await setOrganisationStatus(statusTarget.id, next, reason);
    message.success(t('messages.statusUpdated'));
    setStatusTarget(null);
    refresh();
  };

  /** Reason-gated re-parent (S2.1): the handler recomputes path + descendant depth. */
  const submitMove = async (reason: string) => {
    if (!moveIntent) return;
    await reparentOrganisation(
      moveIntent.nodeId,
      moveIntent.newParentId,
      reason,
      (getUserProfile() as User | null)?.name ?? undefined
    );
    message.success(t('messages.orgMoved'));
    setMoveIntent(null);
    refresh();
  };

  const moveSource = moveIntent
    ? (organisations ?? []).find((o) => o.id === moveIntent.nodeId)
    : undefined;
  const moveTarget = moveIntent?.newParentId
    ? (organisations ?? []).find((o) => o.id === moveIntent.newParentId)
    : undefined;

  const columns: ColumnsType<OrganisationUnit> = [
    { title: t('fields.code'), dataIndex: 'code', width: 110, render: (v: string) => <Text strong>{v}</Text> },
    { title: t('fields.name'), dataIndex: 'name' },
    {
      title: t('fields.kind'),
      dataIndex: 'kind',
      width: 130,
      render: (kind: OrgKind) => <Tag color="purple">{t(`kind.${kind}`)}</Tag>,
    },
    {
      title: t('fields.model'),
      dataIndex: 'model',
      width: 120,
      render: (model: OrgModel) => (
        <Tag color={model === 'franchise' ? 'gold' : 'blue'}>{t(`model.${model}`)}</Tag>
      ),
    },
    { title: t('fields.parent'), dataIndex: 'parentName', width: 180, render: renderDash },
    { title: t('fields.depth'), dataIndex: 'depth', width: 80, align: 'right' },
    {
      title: t('fields.status'),
      dataIndex: 'status',
      width: 120,
      render: (status: OrganisationUnit['status']) => (
        <StatusTag status={status === 'active' ? 'active' : 'inactive'} label={t(`status.${status}`)} />
      ),
    },
    {
      title: t('fields.updatedAt'),
      dataIndex: 'updatedAt',
      width: 160,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'toggle',
                label: record.status === 'active' ? t('actions.deactivate') : t('actions.activate'),
                danger: record.status === 'active',
                style: record.status !== 'active' ? { color: token.colorSuccess } : undefined,
                onClick: () => setStatusTarget(record),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const parentOptions = (organisations ?? [])
    .filter((o) => o.status === 'active')
    .map((o) => ({ label: `${o.code} · ${o.name}`, value: o.id }));

  return (
    <div>
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
        <Text type="secondary">{t('organisations.hint')}</Text>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={() => openCreate()}
        >
          {t('actions.createOrganisation')}
        </Button>
      </div>

      <TableContainer>
        {rbacV2 ? (
          <OrgTreeEditor
            orgs={organisations ?? []}
            loading={loading}
            onAddChild={(parentId) => openCreate(parentId)}
            onMove={(intent) => setMoveIntent(intent)}
            onToggleStatus={(org) => setStatusTarget(org)}
          />
        ) : (
          <Table<OrganisationUnit>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={organisations ?? []}
            rowClassName={(record) => (record.status !== 'active' ? 'inactive-row' : '')}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => String(total) }}
            scroll={{ x: 980 }}
            locale={{ emptyText: <ListEmptyState title={t('empty.organisations')} /> }}
          />
        )}
      </TableContainer>

      <Modal
        open={createOpen}
        title={t('organisations.createTitle')}
        okText={t('actions.create')}
        cancelText={t('actions.cancel')}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        confirmLoading={isSubmitting}
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        maskClosable={false}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
          <div>
            <Text style={formStyles.labelStyle}>{t('fields.code')}</Text>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Input {...field} size="large" style={formStyles.inputStyle} status={errors.code && touchedFields.code ? 'error' : undefined} />
              )}
            />
            {errors.code && touchedFields.code && <Text style={formStyles.errorStyle}>{errors.code.message}</Text>}
          </div>
          <div>
            <Text style={formStyles.labelStyle}>{t('fields.name')}</Text>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input {...field} size="large" style={formStyles.inputStyle} status={errors.name && touchedFields.name ? 'error' : undefined} />
              )}
            />
            {errors.name && touchedFields.name && <Text style={formStyles.errorStyle}>{errors.name.message}</Text>}
          </div>
          <div>
            <Text style={formStyles.labelStyle}>{t('fields.kind')}</Text>
            <Controller
              name="kind"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  size="large"
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    field.onChange(value);
                    trigger('kind');
                  }}
                  options={KINDS.map((k) => ({ label: t(`kind.${k}`), value: k }))}
                />
              )}
            />
          </div>
          <div>
            <Text style={formStyles.labelStyle}>{t('fields.model')}</Text>
            <Controller
              name="model"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  size="large"
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    field.onChange(value);
                    trigger('model');
                  }}
                  options={MODELS.map((m) => ({ label: t(`model.${m}`), value: m }))}
                />
              )}
            />
          </div>
          <div>
            <Text style={formStyles.labelStyle}>{t('fields.parent')}</Text>
            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  size="large"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder={t('organisations.noParent')}
                  options={parentOptions}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </div>
        </div>
      </Modal>

      <ReasonRequiredModal
        open={moveIntent !== null}
        title={t('org.tree.reparentTitle')}
        submitText={t('reasonModal.submit')}
        cancelText={t('reasonModal.cancel')}
        reasonLabel={t('reasonModal.reasonLabel')}
        requiredMessage={t('reasonModal.reasonRequired')}
        reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
        description={
          moveSource
            ? `${moveSource.code} · ${moveSource.name} — ${
                moveTarget
                  ? t('org.tree.reparentTo', { name: moveTarget.name })
                  : t('org.tree.reparentToRoot')
              }`
            : undefined
        }
        onSubmit={submitMove}
        onCancel={() => setMoveIntent(null)}
      />

      <ReasonRequiredModal
        open={statusTarget !== null}
        title={
          statusTarget?.status === 'active'
            ? t('reasonModal.deactivateOrgTitle')
            : t('reasonModal.activateOrgTitle')
        }
        submitText={t('reasonModal.submit')}
        cancelText={t('reasonModal.cancel')}
        reasonLabel={t('reasonModal.reasonLabel')}
        requiredMessage={t('reasonModal.reasonRequired')}
        reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
        description={statusTarget ? `${statusTarget.code} · ${statusTarget.name}` : undefined}
        onSubmit={submitStatus}
        onCancel={() => setStatusTarget(null)}
      />
    </div>
  );
};

export default OrganisationsTab;
